import type { IProduct } from "../../types/entities";
import type {
  CreateProductInput,
  DeletedProductItem,
  PagedResult,
  ProductsPageInput,
  UpdateProductInput,
} from "../WarehouseApi";
import type { WarehouseApiModuleContext } from "./WarehouseApiModuleContext";

type ExtractApiError = (payload: unknown) => string | null;
type NormalizeProduct = (value: unknown) => IProduct;
type NormalizeDeletedProductItem = (value: unknown) => DeletedProductItem;
type NormalizeTypeTags = (value: unknown) => string[];
type ToSafeNonNegativeInt = (value: unknown, fallback: number) => number;
type NormalizePagedPayload = <T>(
  payload: unknown,
  mapItem: (item: unknown) => T,
  fallbackErrorMessage: string,
) => PagedResult<T>;

interface ProductApiModuleDependencies {
  extractApiError: ExtractApiError;
  normalizeProduct: NormalizeProduct;
  normalizeDeletedProductItem: NormalizeDeletedProductItem;
  normalizeTypeTags: NormalizeTypeTags;
  toSafeNonNegativeInt: ToSafeNonNegativeInt;
  normalizePagedPayload: NormalizePagedPayload;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function normalizePhotosInput(input: { photo?: string; photos?: string[] }): {
  photo: string | undefined;
  photos: string[] | undefined;
} {
  const normalizedPhotos = Array.isArray(input.photos)
    ? input.photos
        .map((item) => item.trim())
        .filter((item) => !!item)
        .filter((item, index, array) => array.indexOf(item) === index)
    : [];
  const normalizedPhoto = input.photo?.trim() || "";

  if (normalizedPhoto && !normalizedPhotos.includes(normalizedPhoto)) {
    normalizedPhotos.unshift(normalizedPhoto);
  }

  return {
    photo: normalizedPhotos[0] || undefined,
    photos: normalizedPhotos.length > 0 ? normalizedPhotos : undefined,
  };
}

export class ProductApiModule {
  constructor(
    private readonly context: Pick<
      WarehouseApiModuleContext,
      "getJson" | "sendJson" | "fetchWithAuth"
    >,
    private readonly deps: ProductApiModuleDependencies,
  ) {}

  async getAllProducts(): Promise<IProduct[]> {
    const page = await this.getProductsPage();
    return page.items;
  }

  async getProductsPage(
    input: ProductsPageInput = {},
  ): Promise<PagedResult<IProduct>> {
    const query = new URLSearchParams();

    if (typeof input.limit === "number" && Number.isFinite(input.limit)) {
      query.set("limit", String(Math.max(1, Math.floor(input.limit))));
    }

    if (typeof input.offset === "number" && Number.isFinite(input.offset)) {
      query.set("offset", String(Math.max(0, Math.floor(input.offset))));
    }

    if (input.placement) {
      query.set("placement", input.placement);
    }

    if (input.expiry) {
      query.set("expiry", input.expiry);
    }

    if (
      typeof input.expiringDays === "number" &&
      Number.isFinite(input.expiringDays)
    ) {
      query.set(
        "expiringDays",
        String(Math.max(1, Math.floor(input.expiringDays))),
      );
    }

    if (typeof input.q === "string" && input.q.trim()) {
      query.set("q", input.q.trim());
    }

    if (typeof input.typeTag === "string" && input.typeTag.trim()) {
      query.set("typeTag", input.typeTag.trim());
    }

    if (input.measure) {
      query.set("measure", input.measure);
    }

    const path = query.toString()
      ? `/api/products?${query.toString()}`
      : "/api/products";
    const payload = await this.context.getJson<unknown>(path);

    return this.deps.normalizePagedPayload(
      payload,
      (item) => this.deps.normalizeProduct(item),
      "Некорректный ответ сервера при загрузке продуктов",
    );
  }

  async getProductById(id: string): Promise<IProduct | null> {
    const response = await this.context.fetchWithAuth(
      `/api/products/${encodeURIComponent(id)}`,
      "GET",
    );

    if (response.status === 404) {
      return null;
    }

    const payload = (await response.json()) as unknown;
    if (
      !response.ok ||
      !isRecord(payload) ||
      payload.success !== true ||
      !("data" in payload)
    ) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(
        apiError || "Некорректный ответ сервера при загрузке продукта",
      );
    }

    return this.deps.normalizeProduct(payload.data);
  }

  async searchProducts(query: string): Promise<IProduct[]> {
    const payload = await this.context.getJson<unknown>(
      `/api/products/search?q=${encodeURIComponent(query)}`,
    );

    if (
      !isRecord(payload) ||
      payload.success !== true ||
      !Array.isArray(payload.data)
    ) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(
        apiError || "Некорректный ответ сервера при поиске продуктов",
      );
    }

    return payload.data.map((item) => this.deps.normalizeProduct(item));
  }

  async createProduct(input: CreateProductInput): Promise<IProduct> {
    const typeTags = this.deps.normalizeTypeTags(input.typeTags);
    const media = normalizePhotosInput({
      photo: input.photo,
      photos: input.photos,
    });
    const payload = await this.context.sendJson<unknown>(
      "/api/products",
      "POST",
      {
        name: input.name,
        photo: media.photo,
        photos: media.photos,
        description: input.description || "",
        manufacturingDate: input.manufacturingDate ?? null,
        expiryDate: input.expiryDate ?? null,
        typeTags,
        measure: input.measure,
        quantity: input.quantity,
        parentId: input.parentId.trim(),
      },
    );

    if (
      !isRecord(payload) ||
      payload.success !== true ||
      !("data" in payload)
    ) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(
        apiError || "Некорректный ответ сервера при создании продукта",
      );
    }

    return this.deps.normalizeProduct(payload.data);
  }

  async updateProduct(
    id: string,
    input: UpdateProductInput,
  ): Promise<IProduct> {
    const typeTags = this.deps.normalizeTypeTags(input.typeTags);
    const media = normalizePhotosInput({
      photo: input.photo,
      photos: input.photos,
    });
    const payload = await this.context.sendJson<unknown>(
      `/api/products/${encodeURIComponent(id)}`,
      "PUT",
      {
        name: input.name,
        photo: media.photo,
        photos: media.photos,
        description: input.description || "",
        manufacturingDate: input.manufacturingDate ?? null,
        expiryDate: input.expiryDate ?? null,
        typeTags,
        measure: input.measure,
        quantity: input.quantity,
        parentId: input.parentId ?? null,
      },
    );

    if (
      !isRecord(payload) ||
      payload.success !== true ||
      !("data" in payload)
    ) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(
        apiError || "Некорректный ответ сервера при обновлении продукта",
      );
    }

    return this.deps.normalizeProduct(payload.data);
  }

  async deleteProduct(id: string, deleteReason?: string | null): Promise<void> {
    const normalizedReason = (deleteReason || "").trim();
    const payload = await this.context.sendJson<unknown>(
      `/api/products/${encodeURIComponent(id)}`,
      "DELETE",
      normalizedReason ? { deleteReason: normalizedReason } : undefined,
    );

    this.ensureSuccess(payload, "Не удалось удалить продукт");
  }

  async getDeletedProducts(
    input: { limit?: number; offset?: number } = {},
  ): Promise<PagedResult<DeletedProductItem>> {
    const query = new URLSearchParams();
    if (typeof input.limit === "number" && Number.isFinite(input.limit)) {
      query.set("limit", String(Math.max(1, Math.floor(input.limit))));
    }
    if (typeof input.offset === "number" && Number.isFinite(input.offset)) {
      query.set("offset", String(Math.max(0, Math.floor(input.offset))));
    }

    const path = query.toString()
      ? `/api/products/deleted?${query.toString()}`
      : "/api/products/deleted";
    const payload = await this.context.getJson<unknown>(path);

    if (
      !isRecord(payload) ||
      payload.success !== true ||
      !isRecord(payload.data) ||
      !Array.isArray(payload.data.items)
    ) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(
        apiError ||
          "Некорректный ответ сервера при загрузке удаленных продуктов",
      );
    }

    return {
      items: payload.data.items.map((item) =>
        this.deps.normalizeDeletedProductItem(item),
      ),
      total: this.deps.toSafeNonNegativeInt(payload.data.total, 0),
      offset: this.deps.toSafeNonNegativeInt(payload.data.offset, 0),
      limit:
        payload.data.limit === null || payload.data.limit === undefined
          ? null
          : this.deps.toSafeNonNegativeInt(payload.data.limit, 0),
    };
  }

  async restoreProduct(id: string): Promise<void> {
    const payload = await this.context.sendJson<unknown>(
      `/api/products/${encodeURIComponent(id)}/restore`,
      "POST",
    );
    this.ensureSuccess(payload, "Не удалось восстановить товар");
  }

  async hardDeleteProduct(id: string): Promise<void> {
    const payload = await this.context.sendJson<unknown>(
      `/api/products/${encodeURIComponent(id)}/hard-delete`,
      "DELETE",
    );
    this.ensureSuccess(payload, "Не удалось удалить товар окончательно");
  }

  private ensureSuccess(payload: unknown, fallbackMessage: string): void {
    if (!isRecord(payload) || payload.success !== true) {
      const apiError = this.deps.extractApiError(payload);
      throw new Error(apiError || fallbackMessage);
    }
  }
}
